import React, { useState, useEffect } from 'react';
import { 
  Card, Button, Space, Table, message, Divider, Tooltip, Row, Col, 
  InputNumber, Progress, Input, Select, Radio, Upload, Modal, Form,
  Switch, Tag, Alert
} from 'antd';
import { 
  CopyOutlined, DownloadOutlined, MinusOutlined, PlusOutlined, 
  UploadOutlined, DeleteOutlined, WalletOutlined, SendOutlined
} from '@ant-design/icons';
import { useAccount, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { ethers } from 'ethers';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface ImportedWallet {
  address: string;
  privateKey: string;
  balance?: string;
}

interface TransferTarget {
  index: number;
  address: string;
  amount: string;
  memo?: string;
  balance?: string;
  balanceLoading?: boolean;
}

const BatchTransfer = () => {
  // 基础状态
  const [selectedChain, setSelectedChain] = useState<string>('BSC');
  const [transferMode, setTransferMode] = useState<'single' | 'multiple'>('single');
  const [transferType, setTransferType] = useState<'native' | 'erc20'>('native');
  const [loading, setLoading] = useState(false);
  const [customRpcUrls, setCustomRpcUrls] = useState<{[key: string]: string}>({});
  
  // 钱包相关状态 (多笔交易模式用)
  const [importedWallet, setImportedWallet] = useState<ImportedWallet | null>(null);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [privateKeyInput, setPrivateKeyInput] = useState('');
  
  // 转账相关状态
  const [transferTargets, setTransferTargets] = useState<TransferTarget[]>([
    { index: 1, address: '', amount: '', memo: '' }
  ]);
  const [batchAddressInput, setBatchAddressInput] = useState('');
  const [batchAmountInput, setBatchAmountInput] = useState('');
  const [uniformAmount, setUniformAmount] = useState('');
  const [tokenAddress, setTokenAddress] = useState('');
  const [tokenSymbol, setTokenSymbol] = useState('');
  const [tokenDecimals, setTokenDecimals] = useState(18);
  const [isTokenApproved, setIsTokenApproved] = useState(false);
  const [isQueryingBalances, setIsQueryingBalances] = useState(false);
  
  // 进度状态
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');

  // Web3相关
  const { address: connectedAddress, isConnected } = useAccount();
  const { writeContract, data: hash, isPending } = useWriteContract();
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  });

  // 网络配置
  const NETWORK_CONFIG = {
    BSC: {
      chainId: 56,
      name: 'BSC (Binance Smart Chain)',
      contractAddress: '0xa1D72B498743cf91a4bFb09f3c3751D927f4da30',
      rpcUrl: 'https://bsc-dataseed.binance.org/',
      nativeCurrency: 'BNB',
      blockExplorer: 'https://bscscan.com'
    },
    ETH: {
      chainId: 1,
      name: 'Ethereum',
      contractAddress: '0x7656fFA207f372566Fe3cDc5167149795894f8f7',
      rpcUrl: 'https://ethereum.publicnode.com',
      nativeCurrency: 'ETH',
      blockExplorer: 'https://etherscan.io'
    },
    POLYGON: {
      chainId: 137,
      name: 'Polygon',
      contractAddress: '0x6c40Ee999B99E8dCf5CDbA6fE8fc42813BDA99B8',
      rpcUrl: 'https://polygon-rpc.com',
      nativeCurrency: 'POL',
      blockExplorer: 'https://polygonscan.com'
    },
    BASE: {
      chainId: 8453,
      name: 'Base',
      contractAddress: '0x1f7698593ceDCE9d63bACF20879Ad2089b97f439',
      rpcUrl: 'https://mainnet.base.org',
      nativeCurrency: 'ETH',
      blockExplorer: 'https://basescan.org'
    },
    ARBITRUM: {
      chainId: 42161,
      name: 'Arbitrum One',
      contractAddress: '0x876a0C971e0203E19E05c1B969E488742376Dd45',
      rpcUrl: 'https://arbitrum.blockpi.network/v1/rpc/public',
      nativeCurrency: 'ETH',
      blockExplorer: 'https://arbiscan.io'
    },
    OPTIMISM: {
      chainId: 10,
      name: 'Optimism',
      contractAddress: '0x5a71F71D1611B73DFdc21F55e8002EC0e9b7D8a6',
      rpcUrl: 'https://mainnet.optimism.io',
      nativeCurrency: 'ETH',
      blockExplorer: 'https://optimistic.etherscan.io'
    }
  };

  // 获取当前网络配置
  const getCurrentNetworkConfig = () => NETWORK_CONFIG[selectedChain as keyof typeof NETWORK_CONFIG];
  
  // 获取当前RPC URL (优先使用自定义RPC)
  const getCurrentRpcUrl = (): string => {
    const network = selectedChain as keyof typeof NETWORK_CONFIG;
    return customRpcUrls[network] || NETWORK_CONFIG[network].rpcUrl;
  };

  // 批量转账合约信息
  const BATCH_TRANSFER_CONTRACT = {
    address: getCurrentNetworkConfig()?.contractAddress as `0x${string}`,
    abi: [
        {
            "inputs": [
                {
                    "internalType": "address",
                    "name": "tokenAddress",
                    "type": "address"
                },
                {
                    "internalType": "uint256",
                    "name": "value",
                    "type": "uint256"
                }
            ],
            "name": "emergencyWithdrawErc20",
            "outputs": [],
            "stateMutability": "nonpayable",
            "type": "function"
        },
        {
            "inputs": [],
            "name": "emergencyWithdrawEther",
            "outputs": [],
            "stateMutability": "nonpayable",
            "type": "function"
        },
        {
            "inputs": [
                {
                    "internalType": "address[]",
                    "name": "recipients",
                    "type": "address[]"
                },
                {
                    "internalType": "uint256[]",
                    "name": "values",
                    "type": "uint256[]"
                }
            ],
            "name": "multiSendEther",
            "outputs": [],
            "stateMutability": "payable",
            "type": "function"
        },
        {
            "inputs": [
                {
                    "internalType": "contract IERC20",
                    "name": "token",
                    "type": "address"
                },
                {
                    "internalType": "address[]",
                    "name": "recipients",
                    "type": "address[]"
                },
                {
                    "internalType": "uint256[]",
                    "name": "values",
                    "type": "uint256[]"
                }
            ],
            "name": "multiSendToken",
            "outputs": [],
            "stateMutability": "payable",
            "type": "function"
        },
        {
            "inputs": [
                {
                    "internalType": "uint256",
                    "name": "_fee",
                    "type": "uint256"
                }
            ],
            "name": "setFee",
            "outputs": [],
            "stateMutability": "nonpayable",
            "type": "function"
        },
        {
            "inputs": [],
            "stateMutability": "payable",
            "type": "constructor"
        },
        {
            "inputs": [],
            "name": "fee",
            "outputs": [
                {
                    "internalType": "uint256",
                    "name": "",
                    "type": "uint256"
                }
            ],
            "stateMutability": "view",
            "type": "function"
        }
    ]
  };


  const chainOptions = [
    { value: 'BSC', label: 'BSC (Binance Smart Chain)', disabled: false },
    { value: 'ETH', label: 'Ethereum', disabled: false },
    { value: 'POLYGON', label: 'Polygon', disabled: false },
    { value: 'BASE', label: 'Base', disabled: false },
    { value: 'ARBITRUM', label: 'Arbitrum One', disabled: false },
    { value: 'OPTIMISM', label: 'Optimism', disabled: false },
  ];

  // 统一的组件样式
  const componentStyle = {
    height: '36px',
    borderRadius: '6px',
  };

  const buttonStyle = {
    ...componentStyle,
    minWidth: '80px',
  };

  const primaryButtonStyle = {
    ...componentStyle,
    minWidth: '120px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    color: 'white',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
  };

  // 添加转账目标
  const addTransferTarget = () => {
    const newTarget: TransferTarget = {
      index: transferTargets.length + 1,
      address: '',
      amount: '',
      memo: ''
    };
    setTransferTargets([...transferTargets, newTarget]);
  };

  // 删除转账目标
  const removeTransferTarget = (index: number) => {
    if (transferTargets.length <= 1) {
      message.warning('至少需要保留一个转账目标');
      return;
    }
    const newTargets = transferTargets.filter((_, i) => i !== index);
    // 重新编号
    const reindexedTargets = newTargets.map((target, i) => ({
      ...target,
      index: i + 1
    }));
    setTransferTargets(reindexedTargets);
  };

  // 更新转账目标
  const updateTransferTarget = (index: number, field: keyof TransferTarget, value: string) => {
    const newTargets = [...transferTargets];
    newTargets[index] = { ...newTargets[index], [field]: value };
    setTransferTargets(newTargets);
  };

  // 验证以太坊地址格式
  const isValidAddress = (address: string): boolean => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  // 批量导入地址
  const handleBatchImport = () => {
    if (!batchAddressInput.trim()) {
      message.error('请输入地址');
      return;
    }

    const addresses = batchAddressInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const amounts = batchAmountInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    // 验证地址格式
    const invalidAddresses = addresses.filter(addr => !isValidAddress(addr));
    if (invalidAddresses.length > 0) {
      message.error(`发现${invalidAddresses.length}个无效地址格式`);
      return;
    }

    // 创建新的转账目标
    const newTargets: TransferTarget[] = addresses.map((address, index) => ({
      index: index + 1,
      address: address,
      amount: amounts[index] || uniformAmount || '',
      memo: ''
    }));

    setTransferTargets(newTargets);
    setBatchAddressInput('');
    setBatchAmountInput('');
    setUniformAmount('');
    message.success(`成功导入${newTargets.length}个地址`);
  };

  // 批量设置统一转账数量
  const setUniformAmountForAll = () => {
    if (!uniformAmount.trim()) {
      message.error('请输入转账数量');
      return;
    }

    const newTargets = transferTargets.map(target => ({
      ...target,
      amount: uniformAmount
    }));

    setTransferTargets(newTargets);
    message.success(`已为所有地址设置转账数量: ${uniformAmount}`);
  };

  // 批量查询余额
  const queryAllBalances = async () => {
    if (!transferTargets.some(t => t.address)) {
      message.error('没有有效地址可查询');
      return;
    }

    if (transferType === 'erc20' && !tokenAddress) {
      message.error('请先填写代币合约地址');
      return;
    }

    setIsQueryingBalances(true);
    
    try {
      // 根据转账模式选择不同的Provider
      let provider;
      if (transferMode === 'multiple' && importedWallet) {
        // 多笔交易模式：使用配置的RPC (优先使用自定义RPC)
        const rpcUrl = getCurrentRpcUrl();
        provider = new ethers.JsonRpcProvider(rpcUrl);
      } else {
        // 一笔交易模式：使用用户连接的钱包
        provider = new ethers.BrowserProvider(window.ethereum);
      }
      
      const newTargets = [...transferTargets];

      // 为每个地址设置查询状态
      for (let i = 0; i < newTargets.length; i++) {
        if (newTargets[i].address) {
          newTargets[i].balanceLoading = true;
        }
      }
      setTransferTargets([...newTargets]);

      // 并发查询余额
      const balancePromises = transferTargets.map(async (target, index) => {
        if (!target.address) return { index, balance: '' };

        try {
          if (transferType === 'native') {
            // 查询主币余额
            const balance = await provider.getBalance(target.address);
            return { 
              index, 
                                balance: parseFloat(ethers.formatEther(balance)).toFixed(6) + ' ' + getCurrentNetworkConfig().nativeCurrency 
            };
          } else if (transferType === 'erc20' && tokenAddress) {
            // 查询ERC20代币余额
            try {
              const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
              
              // 首先验证这是一个有效的ERC20合约
              const balance = await tokenContract.balanceOf(target.address);
              const formattedBalance = ethers.formatUnits(balance, tokenDecimals);
              return { 
                index, 
                balance: parseFloat(formattedBalance).toFixed(6) + ` ${tokenSymbol || 'Token'}` 
              };
            } catch (contractError: any) {
              console.error(`ERC20合约调用失败 ${target.address}:`, contractError);
              
              // 提供更具体的错误信息
              if (contractError.message?.includes('invalid address')) {
                return { index, balance: '无效地址' };
              } else if (contractError.message?.includes('balanceOf')) {
                return { index, balance: '非ERC20合约' };
              } else if (contractError.message?.includes('network')) {
                return { index, balance: '网络错误' };
              } else {
                return { index, balance: '合约错误' };
              }
            }
          } else {
            return { index, balance: '未设置代币' };
          }
        } catch (error: any) {
          console.error(`查询地址 ${target.address} 余额失败:`, error);
          
          // 提供更具体的错误信息
          if (error.message?.includes('invalid address')) {
            return { index, balance: '地址无效' };
          } else if (error.message?.includes('network')) {
            return { index, balance: '网络错误' };
          } else {
            return { index, balance: '查询失败' };
          }
        }
      });

      const results = await Promise.all(balancePromises);
      
      // 更新余额结果
      const updatedTargets = [...newTargets];
      results.forEach(result => {
        if (updatedTargets[result.index]) {
          updatedTargets[result.index].balance = result.balance;
          updatedTargets[result.index].balanceLoading = false;
        }
      });

      setTransferTargets(updatedTargets);
      
      const queryType = transferType === 'native' ? getCurrentNetworkConfig().nativeCurrency : tokenSymbol || '代币';
      message.success(`${queryType}余额查询完成`);
    } catch (error) {
      console.error('批量查询余额失败:', error);
      message.error('批量查询余额失败');
    } finally {
      setIsQueryingBalances(false);
      // 清除所有加载状态
      const clearedTargets = transferTargets.map(target => ({
        ...target,
        balanceLoading: false
      }));
      setTransferTargets(clearedTargets);
    }
  };

  // 导入单个钱包
  const handleImportWallet = () => {
    if (!privateKeyInput.trim()) {
      message.error('请输入私钥');
      return;
    }

    try {
      const wallet = new ethers.Wallet(privateKeyInput.trim());
      const newWallet: ImportedWallet = {
        address: wallet.address,
        privateKey: privateKeyInput.trim()
      };
      
      setImportedWallet(newWallet);
      setPrivateKeyInput('');
      setIsImportModalVisible(false);
      message.success(`成功导入钱包: ${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}`);
    } catch (error) {
      message.error('私钥格式错误');
    }
  };

  // ERC20 Token ABI (标准的)
  const ERC20_ABI = [
    {
      "constant": true,
      "inputs": [
        { "name": "_owner", "type": "address" },
        { "name": "_spender", "type": "address" }
      ],
      "name": "allowance",
      "outputs": [{ "name": "", "type": "uint256" }],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "constant": false,
      "inputs": [
        { "name": "_spender", "type": "address" },
        { "name": "_value", "type": "uint256" }
      ],
      "name": "approve",
      "outputs": [{ "name": "", "type": "bool" }],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "constant": true,
      "inputs": [
        { "name": "_owner", "type": "address" }
      ],
      "name": "balanceOf",
      "outputs": [{ "name": "", "type": "uint256" }],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "constant": true,
      "inputs": [],
      "name": "decimals",
      "outputs": [{ "name": "", "type": "uint8" }],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "constant": true,
      "inputs": [],
      "name": "name",
      "outputs": [{ "name": "", "type": "string" }],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "constant": true,
      "inputs": [],
      "name": "symbol",
      "outputs": [{ "name": "", "type": "string" }],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "constant": true,
      "inputs": [],
      "name": "totalSupply",
      "outputs": [{ "name": "", "type": "uint256" }],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "constant": false,
      "inputs": [
        { "name": "_to", "type": "address" },
        { "name": "_value", "type": "uint256" }
      ],
      "name": "transfer",
      "outputs": [{ "name": "", "type": "bool" }],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "constant": false,
      "inputs": [
        { "name": "_from", "type": "address" },
        { "name": "_to", "type": "address" },
        { "name": "_value", "type": "uint256" }
      ],
      "name": "transferFrom",
      "outputs": [{ "name": "", "type": "bool" }],
      "stateMutability": "nonpayable",
      "type": "function"
    }
  ];

  // 检查ERC20代币授权
  const checkTokenApproval = async () => {
    if (!tokenAddress) return;

    // 检查钱包状态
    if (transferMode === 'single' && !connectedAddress) {
      message.error('一笔交易模式需要先连接钱包');
      return;
    }

    if (transferMode === 'multiple' && !importedWallet) {
      message.error('多笔交易模式需要先导入私钥');
      return;
    }
    
    // 验证地址格式
    if (!isValidAddress(tokenAddress)) {
      message.error('代币合约地址格式无效');
      setTokenSymbol('');
      setTokenDecimals(18);
      setIsTokenApproved(false);
      return;
    }
    
    try {
      // 根据转账模式选择不同的Provider和地址
      let provider;
      let walletAddress;
      
      if (transferMode === 'multiple' && importedWallet) {
        // 多笔交易模式：使用配置的RPC和导入的钱包地址 (优先使用自定义RPC)
        const rpcUrl = getCurrentRpcUrl();
        provider = new ethers.JsonRpcProvider(rpcUrl);
        walletAddress = importedWallet.address;
      } else {
        // 一笔交易模式：使用用户连接的钱包
        provider = new ethers.BrowserProvider(window.ethereum);
        walletAddress = connectedAddress;
      }

      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
      
      // 获取代币信息
      const [symbol, decimals, allowance] = await Promise.all([
        tokenContract.symbol(),
        tokenContract.decimals(),
        tokenContract.allowance(walletAddress, BATCH_TRANSFER_CONTRACT.address)
      ]);
      
      setTokenSymbol(symbol);
      setTokenDecimals(Number(decimals));
      
      // 检查授权额度是否足够 (这里简化检查，实际应该计算总转账金额)
      const totalAmount = transferTargets.reduce((sum, target) => {
        return sum + (parseFloat(target.amount) || 0);
      }, 0);
      
      const requiredAmount = ethers.parseUnits(totalAmount.toString(), decimals);
      setIsTokenApproved(allowance >= requiredAmount);
      
      message.success(`代币信息已更新: ${symbol} (${decimals}位小数)`);
    } catch (error: any) {
      console.error('检查授权失败:', error);
      
      // 提供更具体的错误信息
      if (error.message?.includes('invalid address')) {
        message.error('无效的代币合约地址');
      } else if (error.message?.includes('symbol')) {
        message.error('该地址不是有效的ERC20代币合约');
      } else if (error.message?.includes('network')) {
        message.error('网络连接失败，请重试');
      } else {
        message.error('检查代币授权失败，请确认合约地址是否正确');
      }
      
      setTokenSymbol('');
      setTokenDecimals(18);
      setIsTokenApproved(false);
    }
  };

  // 授权ERC20代币
  const approveToken = async () => {
    if (!tokenAddress) {
      message.error('请先填写代币地址');
      return;
    }

    // 检查钱包状态
    if (transferMode === 'single' && !connectedAddress) {
      message.error('一笔交易模式需要先连接钱包');
      return;
    }

    if (transferMode === 'multiple' && !importedWallet) {
      message.error('多笔交易模式需要先导入私钥');
      return;
    }

    try {
      setLoading(true);
      setCurrentStep('正在授权代币...');
      
      // 设置无限授权额度
      const approveAmount = ethers.MaxUint256;
      
      if (transferMode === 'multiple' && importedWallet) {
        // 多笔交易模式：使用私钥直接授权 (优先使用自定义RPC)
        const rpcUrl = getCurrentRpcUrl();
        const provider = new ethers.JsonRpcProvider(rpcUrl);
        const wallet = new ethers.Wallet(importedWallet.privateKey, provider);
        const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, wallet);
        
        const tx = await tokenContract.approve(BATCH_TRANSFER_CONTRACT.address, approveAmount);
        await tx.wait();
        
        message.success('代币授权成功！');
        setIsTokenApproved(true);
        setLoading(false);
        setCurrentStep('');
        
        // 重新检查授权状态
        setTimeout(() => {
          checkTokenApproval();
        }, 2000);
      } else {
        // 一笔交易模式：使用 wagmi 的 writeContract 进行授权
        writeContract({
          address: tokenAddress as `0x${string}`,
          abi: ERC20_ABI,
          functionName: 'approve',
          args: [BATCH_TRANSFER_CONTRACT.address, approveAmount],
        });
      }
      
    } catch (error) {
      console.error('授权失败:', error);
      message.error('授权失败');
      setLoading(false);
      setCurrentStep('');
    }
  };

  // 监听授权交易状态
  useEffect(() => {
    if (isSuccess && currentStep === '正在授权代币...') {
      message.success('代币授权成功！');
      setIsTokenApproved(true);
      setLoading(false);
      setCurrentStep('');
      
      // 重新检查授权状态
      setTimeout(() => {
        checkTokenApproval();
      }, 2000);
    }
  }, [isSuccess, currentStep]);

  // 执行批量转账
  const executeBatchTransfer = async () => {
    // 验证钱包状态
    if (transferMode === 'single' && !isConnected) {
      message.error('一笔交易模式需要先连接钱包');
      return;
    }

    if (transferMode === 'multiple' && !importedWallet) {
      message.error('多笔交易模式需要先导入私钥');
      return;
    }

    if (transferTargets.some(target => !target.address || !target.amount)) {
      message.error('请填写完整的转账信息');
      return;
    }

    if (transferType === 'erc20' && !tokenAddress) {
      message.error('请填写代币合约地址');
      return;
    }

    if (transferType === 'erc20' && !isTokenApproved) {
      message.error('请先授权代币');
      return;
    }

    setLoading(true);
    setProgress(0);
    setCurrentStep('准备转账...');

    try {
      if (transferMode === 'single') {
        // 一笔交易模式 - 使用合约批量转账
        await executeSingleTransaction();
      } else {
        // 多笔交易模式 - 逐个转账
        await executeMultipleTransactions();
      }

      setProgress(100);
      setCurrentStep('转账完成');
    } catch (error) {
      message.error('转账失败');
      console.error('Transfer error:', error);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setProgress(0);
        setCurrentStep('');
      }, 3000);
    }
  };

  // 单笔交易模式
  const executeSingleTransaction = async () => {
    setCurrentStep('发起批量转账交易...');
    setProgress(50);
    
    // 准备转账数据
    const recipients = transferTargets.map(target => target.address as `0x${string}`);
    const amounts = transferTargets.map(target => {
      if (transferType === 'native') {
        return ethers.parseEther(target.amount);
      } else {
        return ethers.parseUnits(target.amount, tokenDecimals);
      }
    });

    // 计算总转账金额（仅用于主币转账时作为value）
    const totalEthValue = transferType === 'native' 
      ? amounts.reduce((sum, amount) => sum + amount, BigInt(0))
      : BigInt(0);

    try {
      if (transferType === 'native') {
        // 转账主币 - 使用批量转账合约
        writeContract({
          address: BATCH_TRANSFER_CONTRACT.address,
          abi: BATCH_TRANSFER_CONTRACT.abi,
          functionName: 'multiSendEther',
          args: [recipients, amounts],
          value: totalEthValue,
        });
      } else {
        // 转账ERC20代币 - 使用批量转账合约
        writeContract({
          address: BATCH_TRANSFER_CONTRACT.address,
          abi: BATCH_TRANSFER_CONTRACT.abi,
          functionName: 'multiSendToken',
          args: [tokenAddress as `0x${string}`, recipients, amounts],
        });
      }
      
      setCurrentStep('等待交易确认...');
      setProgress(75);
    } catch (error) {
      console.error('批量转账失败:', error);
      throw error;
    }
  };

  // 多笔交易模式
  const executeMultipleTransactions = async () => {
    if (!importedWallet) {
      message.error('多笔交易模式需要先导入私钥');
      return;
    }

    try {
      setCurrentStep('开始多笔转账...');
      setProgress(10);
      
      const validTargets = transferTargets.filter(target => 
        target.address && target.amount && parseFloat(target.amount) > 0
      );
      
      let successCount = 0;
      const totalCount = validTargets.length;
      
      // 创建钱包实例
      const networkConfig = getCurrentNetworkConfig();
      const rpcUrl = getCurrentRpcUrl();
      const provider = new ethers.JsonRpcProvider(rpcUrl);
      const wallet = new ethers.Wallet(importedWallet.privateKey, provider);
      
      for (let i = 0; i < validTargets.length; i++) {
        const target = validTargets[i];
        const progressPercent = Math.round(((i + 1) / totalCount) * 90) + 10;
        
        setCurrentStep(`转账中... (${i + 1}/${totalCount})`);
        setProgress(progressPercent);
        
        try {
          let tx;
          
          if (transferType === 'native') {
            // 主币转账
            tx = await wallet.sendTransaction({
              to: target.address,
              value: ethers.parseEther(target.amount)
            });
          } else {
            // ERC20代币转账
            const tokenContract = new ethers.Contract(tokenAddress!, ERC20_ABI, wallet);
            tx = await tokenContract.transfer(
              target.address,
              ethers.parseUnits(target.amount, tokenDecimals)
            );
          }
          
          // 等待交易确认
          await tx.wait();
          successCount++;
          
          await new Promise(resolve => setTimeout(resolve, 1000)); // 每笔交易间隔1秒
        } catch (error) {
          console.error(`转账到 ${target.address} 失败:`, error);
          // 继续处理下一笔交易
        }
      }
      
      setProgress(100);
      setCurrentStep(`转账完成！成功 ${successCount}/${totalCount} 笔`);
      
      if (successCount === totalCount) {
        message.success(`所有转账完成！共 ${successCount} 笔`);
      } else {
        message.warning(`部分转账完成：${successCount}/${totalCount} 笔成功`);
      }
      
    } catch (error) {
      console.error('多笔交易失败:', error);
      message.error('多笔交易失败，请检查网络连接和余额');
      setCurrentStep('转账失败');
    }
  };

  // 监听批量转账交易状态
  useEffect(() => {
    if (isSuccess && (currentStep === '等待交易确认...' || currentStep.includes('批量转账'))) {
      message.success('批量转账成功！');
      setProgress(100);
      setCurrentStep('转账完成');
      setLoading(false);
      
      // 清空转账目标（可选）
      // setTransferTargets([{ index: 1, address: '', amount: '', memo: '' }]);
      
      setTimeout(() => {
        setProgress(0);
        setCurrentStep('');
      }, 3000);
    }
  }, [isSuccess, currentStep]);

  // 复制到剪贴板
  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${type}已复制到剪贴板`);
    } catch (error) {
      message.error('复制失败');
    }
  };



  return (
    <div className="container-responsive">
      {/* 标题 */}
      <h1 style={{ 
        textAlign: 'center', 
        fontSize: 'var(--font-xl)',
        fontWeight: 'bold',
        marginBottom: 'var(--spacing-2xl)',
        color: '#333'
      }}>
        EVM 批量转账工具
      </h1>

      {/* 连接钱包提示 */}
      {!isConnected && (
        <Alert
          message="请先连接钱包"
          description="使用批量转账功能需要先连接您的钱包，请点击右上角的连接按钮"
          type="warning"
          showIcon
          style={{ marginBottom: 'var(--spacing-lg)' }}
        />
      )}

      {/* 基础配置 */}
      <Card className="spacing-responsive" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <h3 style={{ marginBottom: 'var(--spacing-md)', fontSize: 'var(--font-md)', fontWeight: '500' }}>
          基础配置
        </h3>
        
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px' }}>选择网络</h4>
              <Select
                value={selectedChain}
                onChange={(value) => {
                  setSelectedChain(value);
                  // 切换网络时重置代币相关状态
                  setTokenAddress('');
                  setTokenSymbol('');
                  setTokenDecimals(18);
                  setIsTokenApproved(false);
                }}
                style={{ width: '100%', ...componentStyle }}
                options={chainOptions}
              />
              <div style={{ marginTop: '8px', marginBottom: '16px' }}>
                <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: '500' }}>
                  RPC节点 ({selectedChain})
                </div>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder={NETWORK_CONFIG[selectedChain as keyof typeof NETWORK_CONFIG].rpcUrl}
                    value={customRpcUrls[selectedChain as keyof typeof NETWORK_CONFIG] || ''}
                    onChange={(e) => {
                      const url = e.target.value.trim();
                      setCustomRpcUrls(prev => ({
                        ...prev,
                        [selectedChain]: url || ''
                      }));
                      
                      if (url && url !== NETWORK_CONFIG[selectedChain as keyof typeof NETWORK_CONFIG].rpcUrl) {
                        message.info(`已设置${selectedChain}自定义RPC: ${url.slice(0, 20)}${url.length > 20 ? '...' : ''}`);
                      }
                    }}
                    addonBefore="RPC"
                    style={{ fontSize: '12px' }}
                  />
                  <Button
                    onClick={() => {
                      setCustomRpcUrls(prev => {
                        const newUrls = {...prev};
                        delete newUrls[selectedChain];
                        return newUrls;
                      });
                      message.success(`已恢复${selectedChain}默认RPC节点`);
                    }}
                    style={{ minWidth: '60px' }}
                  >
                    重置
                  </Button>
                </Space.Compact>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                  支持 BSC、以太坊、Polygon、Base、Arbitrum、Optimism 网络
                </div>
              </div>
            </div>
          </Col>
          
          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px' }}>转账类型</h4>
              <Radio.Group
                value={transferType}
                onChange={(e) => {
                  setTransferType(e.target.value);
                  // 切换转账类型时清空余额缓存
                  const clearedTargets = transferTargets.map(target => ({
                    ...target,
                    balance: undefined
                  }));
                  setTransferTargets(clearedTargets);
                }}
                style={{ width: '100%' }}
              >
                <Radio.Button value="native" style={buttonStyle}>主币({getCurrentNetworkConfig()?.nativeCurrency || 'Native'})</Radio.Button>
                <Radio.Button value="erc20" style={buttonStyle}>ERC20代币</Radio.Button>
              </Radio.Group>
              
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ marginBottom: '8px' }}>转账模式</h4>
                <Radio.Group
                  value={transferMode}
                  onChange={(e) => setTransferMode(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <Radio.Button value="single" style={buttonStyle}>一笔交易</Radio.Button>
                  <Radio.Button value="multiple" style={buttonStyle}>多笔交易</Radio.Button>
                </Radio.Group>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                  一笔交易节省Gas费，多笔交易更灵活
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Row gutter={[24, 16]} style={{ marginTop: '16px' }}>
          {transferType === 'erc20' && (
            <Col xs={24}>
              <div>
                <h4 style={{ marginBottom: '8px' }}>代币合约地址</h4>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder="输入ERC20代币合约地址"
                    value={tokenAddress}
                    onChange={(e) => setTokenAddress(e.target.value)}
                    style={componentStyle}
                    status={tokenAddress && !isValidAddress(tokenAddress) ? 'error' : undefined}
                  />
                  <Button
                    onClick={checkTokenApproval}
                    style={buttonStyle}
                  >
                    检查
                  </Button>
                </Space.Compact>
                <div style={{ marginTop: '8px' }}>
                  {tokenAddress && !isValidAddress(tokenAddress) && (
                    <div style={{ fontSize: '12px', color: '#ff4d4f' }}>
                      请输入有效的以太坊地址格式 (0x开头，40个十六进制字符)
                    </div>
                  )}
                  {tokenAddress && isValidAddress(tokenAddress) && (
                    <Space>
                      <Tag color={isTokenApproved ? 'green' : 'red'}>
                        {isTokenApproved ? '已授权' : '未授权'}
                      </Tag>
                      {tokenSymbol && (
                        <Tag color="blue">
                          {tokenSymbol}
                        </Tag>
                      )}
                      {!isTokenApproved && (
                        <Button
                          type="link"
                          size="small"
                          onClick={approveToken}
                          loading={loading && currentStep === '正在授权代币...'}
                          disabled={
                            (transferMode === 'single' && !connectedAddress) ||
                            (transferMode === 'multiple' && !importedWallet)
                          }
                          style={{ padding: 0 }}
                        >
                          立即授权
                        </Button>
                      )}
                    </Space>
                  )}
                  {!tokenAddress && (
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      填写代币合约地址后可以查询代币余额和进行代币转账
                    </div>
                  )}
                </div>
              </div>
            </Col>
          )}
        </Row>
      </Card>

      {/* 导入私钥钱包 (仅多笔交易模式显示) */}
      {transferMode === 'multiple' && (
        <Card className="spacing-responsive" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <h3 style={{ 
            marginBottom: 'var(--spacing-md)', 
            fontSize: 'var(--font-md)', 
            fontWeight: '500' 
          }}>
            发送钱包设置
          </h3>
          
          {!importedWallet ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '20px',
              background: '#fafafa',
              borderRadius: '6px',
              border: '1px dashed #d9d9d9'
            }}>
              <div style={{ marginBottom: '12px', color: '#666' }}>
                多笔交易模式需要导入私钥进行转账
              </div>
              <Button
                type="primary"
                icon={<WalletOutlined />}
                onClick={() => setIsImportModalVisible(true)}
                style={buttonStyle}
              >
                导入私钥
              </Button>
            </div>
          ) : (
            <div style={{ 
              padding: '16px',
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: '6px'
            }}>
              <Row align="middle" justify="space-between">
                <Col>
                  <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                    <strong>发送钱包地址:</strong>
                  </div>
                  <div style={{ 
                    fontFamily: 'monospace', 
                    fontSize: '12px',
                    color: '#666'
                  }}>
                    {importedWallet.address}
                  </div>
                  {importedWallet.balance && (
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                      余额: {importedWallet.balance}
                    </div>
                  )}
                </Col>
                <Col>
                  <Space>
                    <Tooltip title="复制地址">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(importedWallet.address, '地址')}
                      />
                    </Tooltip>
                    <Button
                      danger
                      size="small"
                      onClick={() => {
                        setImportedWallet(null);
                        message.success('已移除导入的钱包');
                      }}
                    >
                      移除
                    </Button>
                  </Space>
                </Col>
              </Row>
            </div>
          )}
        </Card>
      )}

      {/* 转账目标 */}
      <Card className="spacing-responsive" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <h3 style={{ 
          marginBottom: 'var(--spacing-md)', 
          fontSize: 'var(--font-md)', 
          fontWeight: '500' 
        }}>
          转账目标配置
        </h3>

        {/* 转账统计 */}
        {transferTargets.some(t => t.address && t.amount) && (
          <div style={{ 
            marginBottom: '16px',
            padding: '12px',
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={8}>
                <strong>转账笔数:</strong> {transferTargets.filter(t => t.address && t.amount).length}
              </Col>
              <Col xs={24} sm={8}>
                <strong>总金额:</strong> {
                  transferTargets
                    .filter(t => t.address && t.amount)
                    .reduce((sum, target) => {
                      const amount = parseFloat(target.amount) || 0;
                      return sum + amount;
                    }, 0).toFixed(6)
                } {transferType === 'native' ? getCurrentNetworkConfig().nativeCurrency : tokenSymbol || 'Token'}
              </Col>
              <Col xs={24} sm={8}>
                <strong>预估Gas费:</strong> {
                  transferMode === 'single' 
                    ? '~0.001' 
                    : `~${(transferTargets.filter(t => t.address && t.amount).length * 0.0005).toFixed(4)}`
                } {getCurrentNetworkConfig().nativeCurrency}
              </Col>
            </Row>
          </div>
        )}

        {/* 批量导入区域 */}
        <div style={{ 
          marginBottom: '16px',
          padding: '16px',
          background: '#fafafa',
          borderRadius: '6px',
          border: '1px dashed #d9d9d9'
        }}>
          <h4 style={{ marginBottom: '12px', fontSize: '14px' }}>批量导入地址</h4>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12}>
              <div style={{ marginBottom: '8px' }}>
                <strong>收款地址 (每行一个)</strong>
              </div>
                             <Input.TextArea
                 placeholder="请输入收款地址，每行一个"
                 value={batchAddressInput}
                 onChange={(e) => setBatchAddressInput(e.target.value)}
                 rows={8}
                 style={{ resize: 'vertical' }}
               />
            </Col>
                         <Col xs={24} sm={12}>
               <div style={{ marginBottom: '8px' }}>
                 <strong>转账数量设置</strong>
               </div>
               
               {/* 统一数量设置 */}
               <div style={{ marginBottom: '8px' }}>
                 <Space.Compact style={{ width: '100%' }}>
                   <Input
                     placeholder={`统一数量 (${transferType === 'native' ? getCurrentNetworkConfig().nativeCurrency : tokenSymbol || 'Token'})`}
                     value={uniformAmount}
                     onChange={(e) => setUniformAmount(e.target.value)}
                     style={{ flex: 1 }}
                   />
                   <Button 
                     type="primary" 
                     onClick={setUniformAmountForAll}
                     disabled={!uniformAmount.trim() || !transferTargets.some(t => t.address)}
                     style={{ minWidth: '80px' }}
                   >
                     统一设置
                   </Button>
                 </Space.Compact>
                 <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                   为所有地址设置相同的转账数量
                 </div>
               </div>

               {/* 个别数量设置 */}
               <div>
                 <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                   或者分别设置 (每行一个，可选)
                 </div>
                 <Input.TextArea
                   placeholder="请输入转账数量，每行一个&#10;例如：&#10;0.1&#10;0.2&#10;如果不填，可以稍后在表格中编辑"
                   value={batchAmountInput}
                   onChange={(e) => setBatchAmountInput(e.target.value)}
                   rows={4}
                   style={{ resize: 'vertical' }}
                 />
               </div>
             </Col>
          </Row>
          <div style={{ marginTop: '12px', textAlign: 'center' }}>
            <Button 
              type="primary" 
              onClick={handleBatchImport}
              disabled={!batchAddressInput.trim()}
              style={buttonStyle}
            >
              导入地址
            </Button>
          </div>
        </div>

        {/* 转账目标表格 */}
        {transferTargets.length > 0 && transferTargets.some(t => t.address) && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <div>
                <h4 style={{ margin: 0 }}>转账列表</h4>
                {transferTargets.some(t => t.balance) && (
                  <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                    {transferType === 'erc20' && !tokenAddress && '请先设置代币地址后查询余额'}
                    {transferType === 'erc20' && tokenAddress && `当前显示 ${tokenSymbol || 'Token'} 余额`}
                    {transferType === 'native' && `当前显示 ${getCurrentNetworkConfig().nativeCurrency} 余额`}
                  </div>
                )}
              </div>
              <Space>
                <Button
                  onClick={queryAllBalances}
                  loading={isQueryingBalances}
                  style={buttonStyle}
                  disabled={transferType === 'erc20' && !tokenAddress}
                >
                  查询{transferType === 'native' ? getCurrentNetworkConfig().nativeCurrency : tokenSymbol || 'Token'}余额
                </Button>
                <Button
                  icon={<PlusOutlined />}
                  onClick={addTransferTarget}
                  style={buttonStyle}
                >
                  添加
                </Button>
                {transferTargets.length > 1 && (
                  <Button
                    danger
                    onClick={() => {
                      setTransferTargets([{ index: 1, address: '', amount: '', memo: '' }]);
                      message.success('已清空所有转账目标');
                    }}
                    style={buttonStyle}
                  >
                    清空
                  </Button>
                )}
              </Space>
            </div>
            
            <Table
              dataSource={transferTargets}
              rowKey="index"
              pagination={false}
              size="small"
              scroll={{ x: 800 }}
              columns={[
                {
                  title: '序号',
                  dataIndex: 'index',
                  key: 'index',
                  width: 60,
                  align: 'center',
                },
                                 {
                   title: '收款地址',
                   dataIndex: 'address',
                   key: 'address',
                   width: 300,
                   render: (text: string, record: TransferTarget, index: number) => (
                     <Space.Compact style={{ width: '100%' }}>
                       <Input
                         value={text}
                         onChange={(e) => updateTransferTarget(index, 'address', e.target.value)}
                         placeholder="0x..."
                         style={{ fontSize: '12px' }}
                         status={text && !isValidAddress(text) ? 'error' : undefined}
                       />
                       {text && isValidAddress(text) && (
                         <Tooltip title="复制地址">
                           <Button
                             type="text"
                             size="small"
                             icon={<CopyOutlined />}
                             onClick={() => copyToClipboard(text, '地址')}
                             style={{ fontSize: '12px' }}
                           />
                         </Tooltip>
                       )}
                     </Space.Compact>
                   ),
                 },
                {
                  title: `转账数量 (${transferType === 'native' ? getCurrentNetworkConfig().nativeCurrency : tokenSymbol || 'Token'})`,
                  dataIndex: 'amount',
                  key: 'amount',
                  width: 120,
                  render: (text: string, record: TransferTarget, index: number) => (
                    <Input
                      value={text}
                      onChange={(e) => updateTransferTarget(index, 'amount', e.target.value)}
                      placeholder="0.0"
                      style={{ fontSize: '12px' }}
                    />
                  ),
                },
                                 {
                   title: `当前余额 (${transferType === 'native' ? getCurrentNetworkConfig().nativeCurrency : tokenSymbol || 'Token'})`,
                   dataIndex: 'balance',
                   key: 'balance',
                   width: 140,
                   render: (text: string, record: TransferTarget) => (
                     <div style={{ fontSize: '12px' }}>
                       {record.balanceLoading ? (
                         <span style={{ color: '#1890ff' }}>查询中...</span>
                       ) : (
                         <span style={{ 
                           color: text && text.includes('查询失败') ? '#ff4d4f' : 
                                  text && text.includes('未设置') ? '#faad14' : '#333'
                         }}>
                           {text || '-'}
                         </span>
                       )}
                     </div>
                   ),
                 },
                {
                  title: '备注',
                  dataIndex: 'memo',
                  key: 'memo',
                  width: 100,
                  render: (text: string, record: TransferTarget, index: number) => (
                    <Input
                      value={text}
                      onChange={(e) => updateTransferTarget(index, 'memo', e.target.value)}
                      placeholder="备注"
                      style={{ fontSize: '12px' }}
                    />
                  ),
                },
                {
                  title: '操作',
                  key: 'action',
                  width: 80,
                  render: (_, record: TransferTarget, index: number) => (
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeTransferTarget(index)}
                      disabled={transferTargets.length <= 1}
                    />
                  ),
                },
              ]}
            />
          </div>
        )}

      </Card>

      {/* 执行转账 */}
      <Card className="spacing-responsive">
        <div style={{ textAlign: 'center' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={executeBatchTransfer}
              loading={loading}
              disabled={
                (transferMode === 'single' && !isConnected) ||
                (transferMode === 'multiple' && !importedWallet) ||
                transferTargets.some(t => !t.address || !t.amount) ||
                (transferType === 'erc20' && (!tokenAddress || !isTokenApproved))
              }
              style={{ 
                ...primaryButtonStyle,
                minWidth: '160px',
                height: '44px',
                fontSize: '16px'
              }}
            >
              开始批量转账
            </Button>
            
            {/* 按钮状态提示 */}
            {transferMode === 'single' && !isConnected && (
              <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                一笔交易模式需要连接钱包
              </div>
            )}
            {transferMode === 'multiple' && !importedWallet && (
              <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                多笔交易模式需要导入私钥
              </div>
            )}
            {((transferMode === 'single' && isConnected) || (transferMode === 'multiple' && importedWallet)) && 
             transferTargets.some(t => !t.address || !t.amount) && (
              <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                请填写完整的转账信息
              </div>
            )}
            {((transferMode === 'single' && isConnected) || (transferMode === 'multiple' && importedWallet)) && 
             transferType === 'erc20' && !tokenAddress && (
              <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                请填写代币合约地址
              </div>
            )}
            {((transferMode === 'single' && isConnected) || (transferMode === 'multiple' && importedWallet)) && 
             transferType === 'erc20' && tokenAddress && !isTokenApproved && (
              <div style={{ fontSize: '12px', color: '#ff4d4f', marginTop: '8px' }}>
                请先授权代币
              </div>
            )}
            
            {loading && (
              <>
                <Progress 
                  percent={progress} 
                  status="active"
                  style={{ maxWidth: 400, margin: '0 auto' }}
                />
                <div style={{ color: '#666', fontSize: '14px' }}>
                  {currentStep}
                </div>
              </>
            )}
          </Space>
        </div>

        {/* 安全提示 */}
        <Alert
          message="安全提醒"
          description={
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>批量转账前请仔细核对收款地址和金额</li>
              <li>建议先小额测试，确认无误后再进行大额转账</li>
              <li>转账过程中请勿关闭页面或断开网络</li>
              <li>保管好您的私钥，不要泄露给任何人</li>
            </ul>
          }
          type="warning"
          showIcon
          style={{ marginTop: '24px' }}
        />
      </Card>

      {/* 导入私钥模态框 */}
      <Modal
        title="导入私钥"
        open={isImportModalVisible}
        onCancel={() => {
          setIsImportModalVisible(false);
          setPrivateKeyInput('');
        }}
        width={500}
        footer={null}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
            私钥
          </div>
          <Input.TextArea
            value={privateKeyInput}
            onChange={(e) => setPrivateKeyInput(e.target.value)}
            placeholder="请输入私钥 (0x开头的64位十六进制字符串)"
            rows={3}
            style={{ fontFamily: 'monospace' }}
          />
        </div>
        
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button 
              onClick={() => {
                setIsImportModalVisible(false);
                setPrivateKeyInput('');
              }}
            >
              取消
            </Button>
            <Button 
              type="primary" 
              onClick={handleImportWallet}
              disabled={!privateKeyInput.trim()}
            >
              导入
            </Button>
          </Space>
        </div>

        <div style={{ 
          marginTop: '16px',
          padding: '12px',
          background: '#fff7e6',
          border: '1px solid #ffd591',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#d46b08'
        }}>
          <strong>安全提醒：</strong> 私钥具有钱包的完全控制权，请确保在安全环境中使用，不要泄露给他人。
        </div>
      </Modal>
    </div>
  );
};

export default BatchTransfer; 